// Discoverability rules — lifted from strongly-ai-frontend's skill scorer.
// They evaluate the metadata that makes a skill findable by users and agents:
// tags (search), category (browse), and mcpTools (connector verification).
//
// Sources of truth, in priority order:
//   1. ctx.skillMetadata (set by the Skill → ScorerInput adapter)
//   2. ctx.parsed.frontmatter (YAML keys: tags, category, mcpTools)
//
// When neither source carries the field at all, the rule returns `skipped` so
// raw-paste skills are not penalised for omitting non-canonical metadata they
// were never expected to declare.

import type { Rule, ScoringContext } from './types';

function readStringArray(ctx: ScoringContext, key: 'tags' | 'mcpTools'): string[] | undefined {
  const fromMeta = ctx.skillMetadata?.[key];
  if (Array.isArray(fromMeta)) return fromMeta.map(String).filter(Boolean);
  const fromFm = ctx.parsed.frontmatter[key];
  if (Array.isArray(fromFm)) return fromFm.map(String).filter(Boolean);
  return undefined;
}

function readString(ctx: ScoringContext, key: 'category'): string | undefined {
  const fromMeta = ctx.skillMetadata?.[key];
  if (typeof fromMeta === 'string' && fromMeta.trim()) return fromMeta.trim();
  const fromFm = ctx.parsed.frontmatter[key];
  if (typeof fromFm === 'string' && fromFm.trim()) return fromFm.trim();
  return undefined;
}

export const discoverabilityRules: Rule[] = [
  {
    id: 'disc.tags',
    category: 'discoverability',
    severity: 'warn',
    weight: 2,
    title: 'Has tags for discoverability',
    appliesTo: 'both',
    guideRef: 'Ch.4 Positioning your skill → Make it findable',
    run(ctx) {
      const tags = readStringArray(ctx, 'tags');
      if (tags === undefined) {
        return { status: 'skipped', message: 'No tags field declared (skill or frontmatter).' };
      }
      if (tags.length >= 2) {
        return { status: 'pass', message: `${tags.length} tag(s) declared.` };
      }
      if (tags.length === 1) {
        return {
          status: 'warn',
          message: 'Only 1 tag — too narrow for discovery.',
          fix: 'Add 2–5 tags spanning domain, workflow type, and integrations so users and agents can find this skill.',
        };
      }
      return {
        status: 'fail',
        message: 'No tags set.',
        fix: 'Add 2–5 relevant tags (domain, workflow type, integrations).',
      };
    },
  },
  {
    id: 'disc.category',
    category: 'discoverability',
    severity: 'info',
    weight: 1,
    title: 'Has a category',
    appliesTo: 'both',
    guideRef: 'Ch.2 Common skill use case categories',
    run(ctx) {
      const cat = readString(ctx, 'category');
      if (cat === undefined) {
        return { status: 'skipped', message: 'No category field declared (skill or frontmatter).' };
      }
      if (cat.length > 0) {
        return { status: 'pass', message: `Category: "${cat}".` };
      }
      return {
        status: 'warn',
        message: 'Category field present but empty.',
        fix: 'Assign a category such as "workflow-automation", "document-creation", or "mcp-enhancement" from the guide\'s use-case categories.',
      };
    },
  },
  {
    id: 'disc.mcpTools',
    category: 'discoverability',
    severity: 'info',
    weight: 2,
    title: 'MCP tools registered when used',
    appliesTo: 'both',
    guideRef: 'Ch.5 MCP connection issues',
    run(ctx) {
      const body = (ctx.parsed.body || '').toLowerCase();
      const mentionsMcp = /\bmcp\b|call\s+[^.]{0,40}\btool\b|tool\s+call/.test(body);
      const declared = readStringArray(ctx, 'mcpTools');
      if (!mentionsMcp) {
        return { status: 'pass', message: 'Skill does not reference MCP or tool calls.' };
      }
      if (declared && declared.length > 0) {
        return { status: 'pass', message: `${declared.length} MCP tool(s) declared.` };
      }
      if (declared === undefined) {
        return {
          status: 'warn',
          message: 'Body mentions MCP / tool calls but no mcpTools metadata declared.',
          fix: 'Add an mcpTools array (frontmatter or skill metadata) listing the exact tool names the skill invokes, so users can verify their connectors.',
        };
      }
      return {
        status: 'warn',
        message: 'Body mentions MCP / tool calls but mcpTools array is empty.',
        fix: 'Populate mcpTools with the exact tool names the skill invokes.',
      };
    },
  },
];
